import {
    useContext,
    createContext,
    PropsWithChildren,
    useState,
    useEffect
} from "react";

import { getTemplateData } from "@/utils/content-queries/get-content-data";
import { getCurrentRoute } from "@/utils/get-current-route";

import { AGGClient } from "../../../clients";
import { AgileArticle } from "../types";

export const AgileArticleContext = createContext<AgileArticle | null>(null);

const useAgileArticleContextValue = (agileArticle: AgileArticle | null) => {
    const [contextValue, setContextValue] = useState(agileArticle);

    useEffect(() => {
        const fetchAgileArticleFromClient = async () => {
            const client = new AGGClient({
                gatewayUrl:
                    process.env["NODE_ENV"] === "production"
                        ? "https://www.atlassian.com/gateway/api/graphql"
                        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
            });

            const { route, locale } = getCurrentRoute();

            const agileArticle = await getTemplateData(route, locale, client);
            setContextValue(agileArticle);
        };

        if (!contextValue) {
            fetchAgileArticleFromClient();
        }
        // empty dependency array so this effect only runs once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return contextValue;
};

export const AgileArticleProvider = (
    props: PropsWithChildren<{ agileArticle: AgileArticle | null }>
) => {
    const value = useAgileArticleContextValue(props.agileArticle);

    return (
        <AgileArticleContext.Provider value={value}>
            {props.children}
        </AgileArticleContext.Provider>
    );
};

export const useAgileArticle = (): AgileArticle | null => {
    const agileArticle = useContext(AgileArticleContext);
    if (!agileArticle) {
        console.warn(
            "SSR failed to fetch content on the server. Fetching content on the client."
        );
    }
    return agileArticle;
};
